import {
  FALLBACK_STITCHING_DEFAULTS,
  pieceStitchLines,
  projectTracingImages,
  type Project,
  type StitchLine,
  type UserStamp,
} from "../model/Project";
import type { StampMeta } from "../stamps/StampMeta";
import type { StampLibrary } from "../stamps/StampLibrary";
import { triggerDownload } from "../exporters/download";

/** Bumped when the on-disk representation needs migration.
 *  v2: slant stitch line `size` reinterpreted from slot long-axis to tooth
 *      width (perpendicular to the seam).
 *  v3: stitching defaults moved up to a top-level `Project.stitchingDefaults`
 *      block (with optional per-piece overrides). Existing lines stay
 *      editable as overrides; the project default is seeded from the first
 *      line we find or from the hardcoded fallback. */
const FILE_VERSION = 3;

export interface ProjectFile {
  fileType: "leather-art";
  version: number;
  project: Project;
  /** Inlined user stamps so the file is self-contained. */
  userStampMetas: StampMeta[];
}

/**
 * Serialize a project to the on-disk JSON representation.
 *
 * `pretty` controls indentation. Human-facing paths (file download / export)
 * keep it `true` for a readable, diff-friendly file. Machine-only paths
 * (autosave) pass `false`: the project embeds tracing-image `data:` base64
 * blobs, and pretty-printing them wastes main-thread CPU and inflates the byte
 * count on every debounced save for an artifact no human ever reads (#743). The
 * compact and pretty outputs parse to the identical object, so
 * `deserializeProject` round-trips both the same way.
 */
export function serializeProject(
  project: Project,
  library: StampLibrary,
  pretty = true,
): string {
  const userStampMetas = project.userStamps
    .map((u) => library.get(u.id))
    .filter((s): s is StampMeta => Boolean(s));
  const file: ProjectFile = {
    fileType: "leather-art",
    version: FILE_VERSION,
    project,
    userStampMetas,
  };
  return JSON.stringify(file, undefined, pretty ? 2 : undefined);
}

export function deserializeProject(text: string): {
  project: Project;
  userStampMetas: StampMeta[];
} {
  const parsed = JSON.parse(text) as ProjectFile;
  if (parsed.fileType !== "leather-art") {
    throw new Error("Not a leather-art project file");
  }
  if (parsed.version > FILE_VERSION) {
    throw new Error(
      `Project file version ${parsed.version} is newer than this app supports`,
    );
  }
  const fromVersion = parsed.version ?? 1;
  const project = migrateProject(parsed.project, fromVersion);
  return {
    project,
    userStampMetas: parsed.userStampMetas ?? [],
  };
}

/** Walk a project loaded from an older file version forward to the current
 *  schema. Each step is intentionally cheap and idempotent so it's safe to
 *  re-run if a future hop introduces another. */
function migrateProject(project: Project, fromVersion: number): Project {
  let p = project;
  if (fromVersion < 2) {
    // v1 stitch lines stored `size` as the slot's long axis. v2 stores it as
    // tooth width (perpendicular to seam). Convert by projecting the long
    // axis onto the seam-perpendicular through |sin(slantAngle)|. Round and
    // diamond chisels keep their meaning (diameter / long axis), so leave
    // those alone.
    p = {
      ...p,
      pieces: p.pieces.map((piece) => ({
        ...piece,
        stitchLines: pieceStitchLines(piece).map((line) => {
          if (line.shape !== "slant") return line;
          const sinAbs = Math.abs(Math.sin((line.slantAngle * Math.PI) / 180));
          if (sinAbs < 0.05) return line;
          return { ...line, size: line.size * sinAbs };
        }),
      })),
    };
  }
  if (fromVersion < 3) {
    // v3 introduces a top-level `Project.stitchingDefaults` block. Seed it
    // from the first stitch line we can find (so the project's default
    // matches what the user already had); fall back to the hardcoded
    // defaults when the project has no stitching at all. Existing lines
    // become explicit overrides — keep their stored values, but flip the
    // override flag on so the cascade doesn't squash them on first edit.
    const firstLine: StitchLine | undefined = p.pieces
      .flatMap((piece) => pieceStitchLines(piece))
      .find(Boolean);
    p = {
      ...p,
      stitchingDefaults: p.stitchingDefaults ?? {
        shape: firstLine?.shape ?? FALLBACK_STITCHING_DEFAULTS.shape,
        pitch: firstLine?.pitch ?? FALLBACK_STITCHING_DEFAULTS.pitch,
        size: firstLine?.size ?? FALLBACK_STITCHING_DEFAULTS.size,
        slantAngle:
          firstLine?.slantAngle ?? FALLBACK_STITCHING_DEFAULTS.slantAngle,
      },
      pieces: p.pieces.map((piece) => ({
        ...piece,
        stitchLines: pieceStitchLines(piece).map((line) => ({
          ...line,
          // Default startOffset to 0 if missing.
          startOffset: line.startOffset ?? 0,
          // Treat any pre-v3 line as an override so its stored values stay
          // visible until the user explicitly resets them to defaults.
          overridesDefaults: line.overridesDefaults ?? true,
        })),
      })),
    };
  }
  return p;
}

/** MIME type for saved project files. RFC 6839 vendor type with the
 *  `+json` structured-syntax suffix:
 *   - Tells tools that respect the suffix the body is JSON-shaped
 *     (so they can syntax-highlight / validate it).
 *   - Distinguishes our files from generic `application/json`, which
 *     iOS / iPadOS recognises and uses to auto-append `.json` to
 *     downloaded files (issue #34). Custom vendor types aren't in
 *     iOS's lookup table, so the OS leaves the filename alone and
 *     the file lands as `<name>.leather` as intended. */
const PROJECT_FILE_MIME = "application/vnd.drawleather+json";

/** Read a blob into a self-contained `data:` URL. */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(blob);
  });
}

/** Prefix of a server-stored (R2-backed) tracing-image URL. */
const API_IMAGE_PREFIX = "/api/images/";

/**
 * Replace every server-stored tracing-image reference with an embedded
 * `data:` URL — in the returned copy only, never in the live project
 * (which keeps its compact server refs). Makes the exported `.leather`
 * file fully self-contained, like the files signed-out users already
 * produce: it opens offline, on other accounts, and — the #952 case —
 * after the server objects have been garbage-collected because the
 * image was later deleted from the live project. Same-`url` duplicates
 * fetch once. Best-effort: a reference that can't be fetched (already
 * deleted, offline) is left as-is rather than blocking the save.
 */
export async function inlineServerImages(project: Project): Promise<Project> {
  const images = projectTracingImages(project);
  const isServer = (u: string | undefined): u is string =>
    !!u && u.startsWith(API_IMAGE_PREFIX);
  if (!images.some((i) => isServer(i.url) || isServer(i.finalUrl))) {
    return project;
  }
  const cache = new Map<string, Promise<string>>();
  const resolve = (url: string): Promise<string> => {
    let hit = cache.get(url);
    if (!hit) {
      hit = (async () => {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return blobToDataUrl(await res.blob());
      })().catch(() => url);
      cache.set(url, hit);
    }
    return hit;
  };
  const inlined = await Promise.all(
    images.map(async (img) => ({
      ...img,
      url: isServer(img.url) ? await resolve(img.url) : img.url,
      finalUrl: isServer(img.finalUrl)
        ? await resolve(img.finalUrl)
        : img.finalUrl,
    })),
  );
  return { ...project, tracingImages: inlined };
}

export async function downloadProjectFile(
  project: Project,
  library: StampLibrary,
) {
  // Self-contained export (#952 review): server image refs are inlined as
  // data URLs so the saved file never depends on R2 objects that a later
  // deletion may garbage-collect.
  const text = serializeProject(await inlineServerImages(project), library);
  const blob = new Blob([text], { type: PROJECT_FILE_MIME });
  await triggerDownload(blob, sanitize(project.name) + ".leather");
}

export async function pickProjectFile(): Promise<{
  project: Project;
  userStampMetas: StampMeta[];
}> {
  // iOS / iPadOS auto-appends `.json` to downloaded files when the
  // Blob's MIME type is `application/json` — so a file we saved as
  // `<name>.leather` lands on disk as `<name>.leather.json`. Accept
  // both extensions on open so iPad users can re-open the files
  // they just saved (issue #34). Save still uses the bare
  // `.leather` extension; only the open dialog filter is permissive.
  const file = await openFileDialog([".leather", ".leather.json"]);
  return readProjectFile(file);
}

/** Parse an already-obtained `.leather` File — shared by the Open
 *  dialog and the screen-wide drop target (#49). */
export async function readProjectFile(file: File): Promise<{
  project: Project;
  userStampMetas: StampMeta[];
}> {
  const text = await file.text();
  return deserializeProject(text);
}

/** True when a dropped file looks like a project file by name — the
 *  `.leather` extension, or the `.leather.json` iOS variant (#34). */
export function isProjectFileName(name: string): boolean {
  const n = name.toLowerCase();
  return n.endsWith(".leather") || n.endsWith(".leather.json");
}

/** iPadOS Safari reports as a desktop Mac ("MacIntel") but is touch-enabled, so
 *  catch both the classic iPhone/iPad user agents and a touch "MacIntel". */
export function isIOS(
  nav: {
    userAgent?: string;
    platform?: string;
    maxTouchPoints?: number;
  } = typeof navigator !== "undefined" ? navigator : {},
): boolean {
  const ua = nav.userAgent ?? "";
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return nav.platform === "MacIntel" && (nav.maxTouchPoints ?? 0) > 1;
}

function openFileDialog(accept: string[]): Promise<File> {
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    // The Files / Google Drive picker on iOS greys out (makes unselectable) any
    // file whose extension has no registered UTI — and our `.leather` is custom,
    // so the file can't be opened from Drive at all (issue #525). Leave `accept`
    // unset on iOS so every file is selectable; the contents are validated on
    // parse regardless. Desktop keeps the extension filter for convenience.
    if (!isIOS()) input.accept = accept.join(",");
    input.onchange = () => {
      const f = input.files?.[0];
      if (f) resolve(f);
      else reject(new Error("No file selected"));
    };
    input.click();
  });
}

export function ensureUserStampRegistered(
  library: StampLibrary,
  metas: StampMeta[],
  project: Project,
): { addedStamps: UserStamp[] } {
  const added: UserStamp[] = [];
  for (const meta of metas) {
    if (!library.get(meta.id)) {
      library.add(meta);
    }
    if (!project.userStamps.find((u) => u.id === meta.id)) {
      added.push({ id: meta.id, name: meta.name, svg: meta.svg });
    }
  }
  return { addedStamps: added };
}

function sanitize(s: string): string {
  return s.replace(/[^a-z0-9_-]/gi, "_") || "leather-art";
}
